import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, SectionHeader, Input, Button, Icon, CustomSelect, Toggle, Modal, MultiSelect, Textarea } from '../components/ui';
import { TimelineField } from '../components/TimelineField';
import { useStaffSettingsState } from '../hooks/useStaffSettingsState';
import {
  deriveAcademicYearLabel,
  readAcademicYearLabelOverride,
} from '../../../settings/academicYear';
import {
  ADMISSIONS_REPORT_CARD_GRADE_SCOPE_OPTIONS,
  getAdmissionsAgreementRows,
  getAdmissionsDocumentRequirementRows,
  mergeAdmissionsDocumentRequirementRows,
  serializeAdmissionsAgreementRows,
  type AdmissionAgreementRow,
  type AdmissionDocumentRequirementRow,
  type AdmissionRequirementMode,
} from '../../../settings/admissionsRequirements';

interface TuitionRateRow {
  id: string;
  divisions: string[];
  grades: string[];
  tuition: string;
  reg: string;
  security: string;
}

interface FeeRule {
  id: number;
  type: 'duration' | 'multichild';
  years?: string;
  childCount?: string;
  discountPercent?: string;
}

interface FeeRow {
  id: string;
  name: string;
  config: string;
  rules: FeeRule[];
  amount: string;
}

const DIVISION_OPTIONS = [
  { label: 'All Divisions', value: 'All Divisions' },
  { label: 'Preschool', value: 'Preschool' },
  { label: 'Boys Division', value: 'Boys Division' },
  { label: 'Girls Division', value: 'Girls Division' },
];

const GRADE_OPTIONS = [
  { label: 'All Grades', value: 'All Grades' },
  { label: 'N', value: 'N' },
  { label: 'K', value: 'K' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
  { label: '8', value: '8' },
];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const toText = (value: unknown): string => String(value ?? '').trim();

const toNumber = (value: string): number => {
  const numeric = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const buildFees = (fees: unknown): FeeRow[] => {
  if (!Array.isArray(fees)) return [];
  return fees.map((entry, index) => {
    const record = asRecord(entry);
    const ext = asRecord(record.ext);
    const rulesRaw = Array.isArray(ext.rules) ? ext.rules : [];
    return {
      id: toText(record.id) || `fee-${index + 1}`,
      name: toText(record.name) || 'Fee',
      config: toText(ext.config) || 'Per Family',
      rules: rulesRaw
        .map((rule, ruleIndex) => {
          const ruleRecord = asRecord(rule);
          const type = toText(ruleRecord.type) === 'multichild' ? 'multichild' : 'duration';
          return {
            id: Number(ruleRecord.id) || ruleIndex + 1,
            type,
            years: toText(ruleRecord.years),
            childCount: toText(ruleRecord.childCount),
            discountPercent: toText(ruleRecord.discountPercent),
          };
        }),
      amount: String(Number(record.amount ?? 0)),
    };
  });
};

const resolveRegistrationFee = (fees: FeeRow[]): string =>
  fees.find((fee) => fee.name.toLowerCase().includes('registration'))?.amount || '0';

const resolveSecurityFee = (fees: FeeRow[]): string =>
  fees.find((fee) => fee.name.toLowerCase().includes('security'))?.amount || '0';

const buildTuitionRows = (tuitionRates: unknown, regAmount: string, securityAmount: string): TuitionRateRow[] => {
  if (!Array.isArray(tuitionRates)) return [];
  return tuitionRates.map((entry, index) => {
    const record = asRecord(entry);
    const gender = toText(record.gender);
    const division = gender === 'boys' ? 'Boys Division' : gender === 'girls' ? 'Girls Division' : 'All Divisions';
    const gradeLabel = toText(record.gradeLabel) || 'All Grades';
    const normalizedGrade = gradeLabel.startsWith('Grade ') ? gradeLabel.replace('Grade ', '') : gradeLabel;
    return {
      id: toText(record.id) || `tuition-${index + 1}`,
      divisions: [division],
      grades: [normalizedGrade],
      tuition: String(Number(record.amount ?? 0)),
      reg: regAmount,
      security: securityAmount,
    };
  });
};

const REQUIREMENT_MODE_OPTIONS = [
  { label: 'Hidden', value: 'hidden' },
  { label: 'Optional', value: 'optional' },
  { label: 'Required', value: 'required' },
] as const;

const toTranscriptGradeToken = (grade: string): string =>
  grade.replace(/^Grade\s+/i, '').trim();

const applyRequirementMode = <T extends { mode: AdmissionRequirementMode; visible: boolean; required: boolean }>(
  row: T,
  mode: AdmissionRequirementMode,
): T => ({
  ...row,
  mode,
  visible: mode !== 'hidden',
  required: mode === 'required',
});

// Sub-component: document row with hidden / optional / required controls.
// Defined outside the parent to avoid recreation on every render.
interface DocRowProps {
  row: AdmissionDocumentRequirementRow;
  onModeChange: (mode: AdmissionRequirementMode) => void;
  onGradeToggle: (grade: string) => void;
}

const DocRow: React.FC<DocRowProps> = ({
  row, onModeChange, onGradeToggle,
}) => (
  <div data-testid={`settings-admissions-doc-${row.settingId}`} className="py-4 border-b border-border last:border-b-0">
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_180px] lg:items-start">
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-irongrey">{row.label}</span>
        <p className="mt-0.5 text-xs text-text-secondary">{row.description}</p>
        {row.settingId === 'transcripts' ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {ADMISSIONS_REPORT_CARD_GRADE_SCOPE_OPTIONS.map((grade) => {
              const isActive = row.gradeScope.includes(grade);
              return (
                <button
                  key={grade}
                  type="button"
                  onClick={() => onGradeToggle(grade)}
                  className={`rounded border px-2 py-1 text-xs transition-colors ${
                    isActive
                      ? 'border-primary/30 bg-[#EAF8F4] text-primary'
                      : 'border-border bg-muted text-text-secondary'
                  }`}
                >
                  {grade}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      <div>
        <CustomSelect
          testId={`settings-admissions-doc-${row.settingId}-mode`}
          options={[...REQUIREMENT_MODE_OPTIONS]}
          value={row.mode}
          onChange={(value) => onModeChange(value as AdmissionRequirementMode)}
        />
      </div>
    </div>
  </div>
);

export default function Admissions() {
  const { settingsState, persistPatch, isSaving } = useStaffSettingsState();

  const [documentRequirements, setDocumentRequirements] = useState<AdmissionDocumentRequirementRow[]>([]);
  const [agreementRequirements, setAgreementRequirements] = useState<AdmissionAgreementRow[]>([]);
  const documentRequirementsRef = useRef(documentRequirements);
  const agreementRequirementsRef = useRef(agreementRequirements);
  const [fees, setFees] = useState<FeeRow[]>([]);
  const [tuitionRates, setTuitionRates] = useState<TuitionRateRow[]>([]);
  const [admissionDates, setAdmissionDates] = useState({
    openDate: '',
    closeDate: '',
    decisionDate: '',
    status: 'open',
  });
  const [academicYearOverride, setAcademicYearOverride] = useState('');
  const admissionDatesRef = useRef(admissionDates);
  const academicYearOverrideRef = useRef(academicYearOverride);

  const [discountFootnote, setDiscountFootnote] = useState('');
  const [isFeeModalOpen, setIsFeeModalOpen] = useState(false);
  const [editingFee, setEditingFee] = useState<FeeRow | null>(null);
  const [feeForm, setFeeForm] = useState<Omit<FeeRow, 'id'>>({ name: '', config: 'Per Family', rules: [], amount: '' });

  useEffect(() => {
    const nextDocumentRequirements = getAdmissionsDocumentRequirementRows(settingsState.admissionConfig);
    const nextAgreementRequirements = getAdmissionsAgreementRows(settingsState.admissionConfig);
    documentRequirementsRef.current = nextDocumentRequirements;
    agreementRequirementsRef.current = nextAgreementRequirements;
    setDocumentRequirements(nextDocumentRequirements);
    setAgreementRequirements(nextAgreementRequirements);
    setAdmissionDates({
      openDate: settingsState.admissionDates.openDate,
      closeDate: settingsState.admissionDates.closeDate,
      decisionDate: settingsState.admissionDates.decisionDate,
      status: settingsState.admissionDates.status,
    });
    admissionDatesRef.current = {
      openDate: settingsState.admissionDates.openDate,
      closeDate: settingsState.admissionDates.closeDate,
      decisionDate: settingsState.admissionDates.decisionDate,
      status: settingsState.admissionDates.status,
    };
    const nextAcademicYearOverride = readAcademicYearLabelOverride(settingsState.academicYear);
    academicYearOverrideRef.current = nextAcademicYearOverride;
    setAcademicYearOverride(nextAcademicYearOverride);

    const nextFees = buildFees(settingsState.fees);
    const regAmount = resolveRegistrationFee(nextFees);
    const securityAmount = resolveSecurityFee(nextFees);
    setFees(nextFees);
    setTuitionRates(buildTuitionRows(settingsState.tuitionRates, regAmount, securityAmount));
  }, [settingsState]);

  const statusBadge = useMemo(() => {
    if (admissionDates.status === 'closed') {
      return { label: 'Closed', className: 'bg-error-background text-strawberry' };
    }
    if (admissionDates.status === 'coming_soon') {
      return { label: 'Coming Soon', className: 'bg-muted text-irongrey' };
    }
    return { label: 'Open', className: 'bg-success-background text-jungle' };
  }, [admissionDates.status]);

  const tuitionRateCategories = useMemo(
    () => settingsState.tuitionRateCategories ?? [],
    [settingsState.tuitionRateCategories],
  );
  const enabledTuitionPaymentPlans = useMemo(
    () => (settingsState.tuitionPaymentPlans ?? []).filter((plan) => plan.enabled),
    [settingsState.tuitionPaymentPlans],
  );
  const familySelfSchedulingEnabled =
    settingsState.admissionConfig.interviewScheduling?.familySelfSchedulingEnabled === true;

  const persistAdmissionDates = async (nextDates: typeof admissionDates) => {
    await persistPatch((current) => {
      current.admissionDates = {
        ...current.admissionDates,
        openDate: nextDates.openDate,
        closeDate: nextDates.closeDate,
        decisionDate: nextDates.decisionDate,
        status: nextDates.status as 'coming_soon' | 'open' | 'closed',
      };
      return current;
    });
  };

  const persistAcademicYearOverride = async (nextOverride: string) => {
    const normalizedOverride = nextOverride.trim();
    await persistPatch((current) => {
      current.academicYear = {
        ...current.academicYear,
        labelOverride: normalizedOverride || null,
      } as typeof current.academicYear;
      return current;
    });
  };

  const updateAdmissionDate = (
    field: 'openDate' | 'closeDate' | 'decisionDate',
    value: string,
  ) => {
    const nextDates = {
      ...admissionDatesRef.current,
      [field]: value,
    };
    admissionDatesRef.current = nextDates;
    setAdmissionDates(nextDates);
    void persistAdmissionDates(nextDates);
  };

  const updateAcademicYearOverride = (value: string) => {
    academicYearOverrideRef.current = value;
    setAcademicYearOverride(value);
  };

  const persistAdmissionConfig = async (
    nextDocuments: AdmissionDocumentRequirementRow[],
    nextAgreements: AdmissionAgreementRow[],
  ) => {
    const transcriptRow = nextDocuments.find((row) => row.settingId === 'transcripts');
    await persistPatch((current) => {
      current.admissionConfig = {
        ...current.admissionConfig,
        requiredDocuments: mergeAdmissionsDocumentRequirementRows(
          current.admissionConfig.requiredDocuments,
          nextDocuments,
        ),
        transcriptsRequired: transcriptRow?.required ?? false,
        transcriptGrades: (transcriptRow?.gradeScope ?? []).map(toTranscriptGradeToken),
        applicationAgreements: serializeAdmissionsAgreementRows(nextAgreements),
      };
      return current;
    });
  };

  const updateFamilySelfSchedulingEnabled = (enabled: boolean) => {
    void persistPatch((current) => {
      current.admissionConfig = {
        ...current.admissionConfig,
        interviewScheduling: {
          ...current.admissionConfig.interviewScheduling,
          familySelfSchedulingEnabled: enabled,
        },
      };
      return current;
    });
  };

  const persistFees = async (nextFees: FeeRow[]) => {
    await persistPatch((current) => {
      current.fees = nextFees.map((fee) => ({
        id: fee.id,
        name: fee.name,
        amount: toNumber(fee.amount),
        ext: {
          config: fee.config,
          rules: fee.rules,
        },
      }));
      return current;
    });
  };

  const persistTuitionRates = async (nextRates: TuitionRateRow[]) => {
    await persistPatch((current) => {
      current.tuitionRates = nextRates.map((rate) => {
        const division = rate.divisions[0] || 'All Divisions';
        const gender = division === 'Boys Division' ? 'boys' : division === 'Girls Division' ? 'girls' : 'all';
        const grade = rate.grades[0] || 'All Grades';
        return {
          id: rate.id,
          gradeLabel: grade.startsWith('Grade ') ? grade : `Grade ${grade}`,
          gender: gender as 'all' | 'boys' | 'girls',
          amount: toNumber(rate.tuition),
        };
      });

      const registrationFee = nextRates[0]?.reg;
      const securityFee = nextRates[0]?.security;
      current.fees = current.fees.map((fee) => {
        const lowerName = fee.name.toLowerCase();
        if (registrationFee && lowerName.includes('registration')) {
          return { ...fee, amount: toNumber(registrationFee) };
        }
        if (securityFee && lowerName.includes('security')) {
          return { ...fee, amount: toNumber(securityFee) };
        }
        return fee;
      });
      return current;
    });
  };

  const openFeeModal = (fee?: FeeRow) => {
    if (fee) {
      setEditingFee(fee);
      setFeeForm({ name: fee.name, config: fee.config, rules: [...fee.rules], amount: fee.amount });
    } else {
      setEditingFee(null);
      setFeeForm({ name: '', config: 'Per Family', rules: [], amount: '' });
    }
    setIsFeeModalOpen(true);
  };

  const addRule = () => {
    if (feeForm.rules.length >= 3) return;
    setFeeForm((previous) => ({
      ...previous,
      rules: [...previous.rules, { id: Date.now(), type: 'duration', years: '' }],
    }));
  };

  const removeRule = (id: number) => {
    setFeeForm((previous) => ({
      ...previous,
      rules: previous.rules.filter((rule) => rule.id !== id),
    }));
  };

  const updateRule = (id: number, updates: Partial<FeeRule>) => {
    setFeeForm((previous) => ({
      ...previous,
      rules: previous.rules.map((rule) => (rule.id === id ? { ...rule, ...updates } : rule)),
    }));
  };

  const handleFeeSubmit = () => {
    const nextFees = editingFee
      ? fees.map((fee) => (fee.id === editingFee.id ? { ...fee, ...feeForm } : fee))
      : [...fees, { ...feeForm, id: `fee-${Date.now()}` }];
    setFees(nextFees);
    setIsFeeModalOpen(false);
    void persistFees(nextFees);
  };

  const addTuitionRate = () => {
    const nextRate: TuitionRateRow = {
      id: `tuition-${Date.now()}`,
      divisions: [],
      grades: [],
      tuition: '0',
      reg: tuitionRates[0]?.reg || '0',
      security: tuitionRates[0]?.security || '0',
    };
    const nextRates = [...tuitionRates, nextRate];
    setTuitionRates(nextRates);
    void persistTuitionRates(nextRates);
  };

  const updateTuitionRate = (rateId: string, patch: Partial<TuitionRateRow>) => {
    setTuitionRates((previous) =>
      previous.map((rate) => (rate.id === rateId ? { ...rate, ...patch } : rate))
    );
  };

  const updateSharedTuitionField = (field: 'reg' | 'security', value: string) => {
    setTuitionRates((previous) =>
      previous.map((rate) => ({ ...rate, [field]: value }))
    );
  };

  const removeTuitionRate = (rateId: string) => {
    const nextRates = tuitionRates.filter((entry) => entry.id !== rateId);
    setTuitionRates(nextRates);
    void persistTuitionRates(nextRates);
  };

  const removeChip = (rateId: string, type: 'divisions' | 'grades', value: string) => {
    const nextRates = tuitionRates.map((rate) =>
      rate.id === rateId ? { ...rate, [type]: rate[type].filter((entry) => entry !== value) } : rate
    );
    setTuitionRates(nextRates);
    void persistTuitionRates(nextRates);
  };

  const addToRate = (rateId: string, type: 'divisions' | 'grades', value: string) => {
    const nextRates = tuitionRates.map((rate) => {
      if (rate.id !== rateId || rate[type].includes(value)) {
        return rate;
      }
      return { ...rate, [type]: [...rate[type], value] };
    });
    setTuitionRates(nextRates);
    void persistTuitionRates(nextRates);
  };

  const updateDocumentRequirement = (
    settingId: string,
    updater: (row: AdmissionDocumentRequirementRow) => AdmissionDocumentRequirementRow,
  ) => {
    const nextDocuments = documentRequirementsRef.current.map((row) =>
      row.settingId === settingId ? updater(row) : row,
    );
    documentRequirementsRef.current = nextDocuments;
    setDocumentRequirements(nextDocuments);
    void persistAdmissionConfig(nextDocuments, agreementRequirementsRef.current);
  };

  const updateAgreementRequirement = (
    agreementId: string,
    updater: (row: AdmissionAgreementRow) => AdmissionAgreementRow,
    options: { persist?: boolean } = {},
  ) => {
    const nextAgreements = agreementRequirementsRef.current.map((row) =>
      row.id === agreementId ? updater(row) : row,
    );
    agreementRequirementsRef.current = nextAgreements;
    setAgreementRequirements(nextAgreements);
    if (options.persist !== false) {
      void persistAdmissionConfig(documentRequirementsRef.current, nextAgreements);
    }
  };

  const persistCurrentAdmissionConfig = () => {
    void persistAdmissionConfig(documentRequirementsRef.current, agreementRequirementsRef.current);
  };

  const autoAcademicYearLabel = deriveAcademicYearLabel(
    new Date(),
    settingsState.academicYear.startDate,
  );

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      <Card>
        <SectionHeader title="Application Dates" description="Control the admissions timeline." />
        <div className="p-0 pb-2 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <TimelineField
              testId="settings-admissions-timeline"
              openDate={admissionDates.openDate}
              closeDate={admissionDates.closeDate}
              decisionDate={admissionDates.decisionDate}
              onOpenDateChange={(value) => updateAdmissionDate('openDate', value)}
              onCloseDateChange={(value) => updateAdmissionDate('closeDate', value)}
              onDecisionDateChange={(value) => updateAdmissionDate('decisionDate', value)}
            />
          </div>
          <div className="bg-muted rounded-lg p-6 border border-border h-fit">
            <h4 className="text-sm font-medium text-irongrey mb-4">Current System Status</h4>
            <div className="mb-6">
              <CustomSelect
                testId="settings-admissions-status-control"
                options={[
                  { label: 'Open', value: 'open' },
                  { label: 'Coming Soon', value: 'coming_soon' },
                  { label: 'Closed', value: 'closed' },
                ]}
                value={admissionDates.status}
                onChange={(value) => {
                  const next = { ...admissionDates, status: value };
                  setAdmissionDates(next);
                  void persistAdmissionDates(next);
                }}
              />
            </div>
            <div className="bg-white border border-border rounded p-4">
              <p className="text-xs text-text-secondary mb-2">Preview Badge</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}>{statusBadge.label}</span>
              <p className="text-xs text-text-secondary mt-2">This status badge will appear on the parent portal landing page.</p>
            </div>
            <div className="bg-white border border-border rounded p-4 mt-4">
              <Input
                data-testid="settings-admissions-academic-year-override"
                label="Academic Year Override"
                subLabel={`Leave blank to auto-roll the public apply banner. Current auto value: ${autoAcademicYearLabel}.`}
                placeholder={autoAcademicYearLabel}
                value={academicYearOverride}
                onChange={(event) => updateAcademicYearOverride(event.target.value)}
                onBlur={() => void persistAcademicYearOverride(academicYearOverrideRef.current)}
              />
            </div>
          </div>
        </div>
      </Card>

      <Card data-testid="settings-admissions-scheduling">
        <SectionHeader
          title="Interview Scheduling"
          description="Control whether families can choose an admissions interview time from the application flow."
        />
        <div className="rounded-lg border border-border bg-muted/40 p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h4 className="text-sm font-medium text-irongrey">
                Allow families to self-schedule admissions interviews
              </h4>
              <p className="mt-1 max-w-3xl text-xs leading-5 text-text-secondary">
                When off, families submit admissions applications without choosing an interview time.
                Admissions dashboard scheduling prompts are hidden, but existing schedule history
                remains visible in applicant records.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={familySelfSchedulingEnabled}
              aria-disabled={isSaving}
              aria-label="Allow families to self-schedule admissions interviews"
              data-testid="settings-admissions-family-self-scheduling-toggle"
              disabled={isSaving}
              onClick={() => updateFamilySelfSchedulingEnabled(!familySelfSchedulingEnabled)}
              className={`${familySelfSchedulingEnabled ? 'bg-primary' : 'bg-border'} ${isSaving ? 'cursor-wait opacity-70' : 'cursor-pointer'} relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            >
              <span
                aria-hidden="true"
                className={`${familySelfSchedulingEnabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>
      </Card>

      <Card>
        <SectionHeader title="Application Requirements" description="Choose whether each application item is hidden, optional, or required." />
        <fieldset className="border-0 p-0 m-0">
          <legend className="sr-only">Application document requirements</legend>
          <div className="divide-y divide-border">
            {documentRequirements.map((row) => (
              <DocRow
                key={row.settingId}
                row={row}
                onModeChange={(mode) =>
                  updateDocumentRequirement(row.settingId, (current) =>
                    applyRequirementMode(current, mode),
                  )
                }
                onGradeToggle={(grade) =>
                  updateDocumentRequirement(row.settingId, (current) => {
                    const gradeScope = current.gradeScope.includes(grade)
                      ? current.gradeScope.filter((entry) => entry !== grade)
                      : [...current.gradeScope, grade];
                    return { ...current, gradeScope };
                  })
                }
              />
            ))}
          </div>
        </fieldset>
      </Card>

      <Card data-testid="settings-admissions-agreements">
        <SectionHeader
          title="Application Agreements"
          description="Set the policy and certification language families see during the application."
        />
        <div className="space-y-6">
          {agreementRequirements.map((agreement) => (
            <div
              key={agreement.id}
              data-testid={`settings-admissions-agreement-${agreement.id}`}
              className="rounded-lg border border-border p-4"
            >
              <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_180px] lg:items-start">
                <div>
                  <h4 className="text-sm font-medium text-irongrey">{agreement.label}</h4>
                  <p className="mt-1 text-xs text-text-secondary">
                    {agreement.id === 'tuition_policy'
                      ? 'Usually shown later in the enrollment contract flow; keep it hidden here unless the application should collect this acknowledgement.'
                      : 'Required certification shown before the family submits the application.'}
                  </p>
                </div>
                <CustomSelect
                  testId={`settings-admissions-agreement-${agreement.id}-mode`}
                  options={[...REQUIREMENT_MODE_OPTIONS]}
                  value={agreement.mode}
                  onChange={(value) =>
                    updateAgreementRequirement(agreement.id, (current) =>
                      applyRequirementMode(current, value as AdmissionRequirementMode),
                    )
                  }
                />
              </div>
              <Textarea
                value={agreement.text}
                onChange={(event) =>
                  updateAgreementRequirement(
                    agreement.id,
                    (current) => ({ ...current, text: event.target.value }),
                    { persist: false },
                  )
                }
                onBlur={persistCurrentAdmissionConfig}
              />
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <SectionHeader
          title="Tuition & Fees"
          description="Configure tuition rates and fee types."
        />

        <div className="space-y-8">
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-semibold text-irongrey">Fee Types</h4>
              <Button data-testid="settings-admissions-add-fee-button" variant="outline" onClick={() => openFeeModal()}><Icon name="Plus" className="w-4 h-4 mr-1" /> Add Fee</Button>
            </div>
            <p className="text-xs text-text-secondary mb-4 -mt-2">Configure additional fees and their rules.</p>
            <p
              className="mb-4 text-xs font-medium text-jungle/80"
              data-testid="settings-admissions-registration-fee-helper"
            >
              The registration fee row is the live per-child amount used on the parent enrollment payment step.
            </p>
            <div
              data-testid="settings-admissions-fee-table-scroller"
              className="overflow-x-auto rounded border border-border"
            >
              <table
                data-testid="settings-admissions-fee-table"
                className="table-row-hover-surface table-sticky-last-action-col-md-up w-full min-w-[820px] divide-y divide-border"
                style={{
                  '--triedge-table-row-hover-bg': '#f8faf9',
                  '--triedge-sticky-last-col-hover-bg': '#f8faf9',
                } as React.CSSProperties}
              >
                <thead className="bg-muted">
                  <tr>
                    <th className="min-w-[280px] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary md:px-6">Fee Name</th>
                    <th className="w-[152px] min-w-[152px] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary md:px-6">Configuration</th>
                    <th className="w-[148px] min-w-[148px] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary md:px-6">Rules</th>
                    <th className="w-[112px] min-w-[112px] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-secondary md:px-6">Amount</th>
                    <th className="w-[112px] min-w-[112px] px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-secondary md:px-6">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-border">
                  {fees.map((fee) => (
                    <tr key={fee.id} data-testid={`settings-admissions-fee-row-${fee.id}`}>
                      <td className="min-w-[280px] px-4 py-4 whitespace-nowrap text-sm font-medium text-irongrey md:px-6">{fee.name}</td>
                      <td className="w-[152px] min-w-[152px] px-4 py-4 whitespace-nowrap text-sm text-text-secondary md:px-6">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-irongrey">{fee.config}</span>
                      </td>
                      <td className="w-[148px] min-w-[148px] px-4 py-4 whitespace-nowrap text-sm text-text-secondary md:px-6">
                        {fee.rules.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {fee.rules.map((rule) => (
                              <span key={rule.id} className="text-xs">
                                {rule.type === 'duration'
                                  ? `Duration: First ${rule.years} years`
                                  : `Multi-child: ${rule.childCount}+ kids, ${rule.discountPercent}% off`}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-text-tertiary italic">No rules</span>
                        )}
                      </td>
                      <td className="w-[112px] min-w-[112px] px-4 py-4 whitespace-nowrap text-sm text-irongrey md:px-6">${fee.amount}</td>
                      <td className="w-[112px] min-w-[112px] px-4 py-4 whitespace-nowrap align-middle text-right text-sm font-medium md:px-6">
                        <div className="flex justify-end gap-3">
                          <button
                            data-testid={`settings-admissions-fee-edit-${fee.id}`}
                            className="text-text-tertiary hover:text-primary"
                            onClick={() => openFeeModal(fee)}
                          >
                            <Icon name="Edit2" className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`settings-admissions-fee-delete-${fee.id}`}
                            onClick={() => {
                              const nextFees = fees.filter((entry) => entry.id !== fee.id);
                              setFees(nextFees);
                              void persistFees(nextFees);
                            }}
                            className="text-text-tertiary hover:text-strawberry"
                          >
                            <Icon name="Trash2" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div data-testid="settings-admissions-tuition-table" className="border-t border-border pt-8">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-sm font-semibold text-irongrey">Annual Tuition Rates</h4>
              <Button data-testid="settings-admissions-tuition-add-row" variant="outline" onClick={addTuitionRate}><Icon name="Plus" className="w-4 h-4 mr-1" /> Add Tuition Rate</Button>
            </div>

            <div
              data-testid="settings-admissions-tuition-mobile-list"
              className="space-y-3 md:hidden"
            >
              {tuitionRates.map((rate) => (
                <div
                  key={rate.id}
                  data-testid={`settings-admissions-tuition-mobile-card-${rate.id}`}
                  className="space-y-4 rounded-md border border-border bg-muted p-4"
                >
                  <div>
                    <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-secondary">
                      Divisions
                    </p>
                    <MultiSelect
                      options={DIVISION_OPTIONS}
                      value={rate.divisions}
                      onChange={(value) => addToRate(rate.id, 'divisions', value)}
                      onRemove={(value) => removeChip(rate.id, 'divisions', value)}
                      placeholder="Select Divisions..."
                    />
                  </div>

                  <div>
                    <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-secondary">
                      Grades
                    </p>
                    <MultiSelect
                      options={GRADE_OPTIONS}
                      value={rate.grades}
                      onChange={(value) => addToRate(rate.id, 'grades', value)}
                      onRemove={(value) => removeChip(rate.id, 'grades', value)}
                      placeholder="Select Grades..."
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label className="block">
                      <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Annual Tuition
                      </span>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-sm text-text-secondary">$</span>
                        <input
                          className="block h-9 w-full rounded border border-border pl-7 text-sm"
                          type="text"
                          value={rate.tuition}
                          onChange={(event) => updateTuitionRate(rate.id, { tuition: event.target.value })}
                          onBlur={() => void persistTuitionRates(tuitionRates)}
                        />
                      </div>
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Registration Fee
                      </span>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-sm text-text-secondary">$</span>
                        <input
                          className="block h-9 w-full rounded border border-border pl-7 text-sm"
                          type="text"
                          value={rate.reg}
                          onChange={(event) => updateSharedTuitionField('reg', event.target.value)}
                          onBlur={() => void persistTuitionRates(tuitionRates)}
                        />
                      </div>
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Security Fee
                      </span>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-sm text-text-secondary">$</span>
                        <input
                          className="block h-9 w-full rounded border border-border pl-7 text-sm"
                          type="text"
                          value={rate.security}
                          onChange={(event) => updateSharedTuitionField('security', event.target.value)}
                          onBlur={() => void persistTuitionRates(tuitionRates)}
                        />
                      </div>
                    </label>
                  </div>

                  <div className="border-t border-border pt-3">
                    <Button
                      variant="ghost"
                      onClick={() => removeTuitionRate(rate.id)}
                      className="w-full text-strawberry hover:text-strawberry/80"
                    >
                      <Icon name="Trash2" className="mr-2 h-4 w-4" />
                      Remove Rate
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block">
              <div className="mb-2 grid grid-cols-12 gap-4 px-2">
                <div className="col-span-3 text-xs font-medium text-text-secondary uppercase tracking-wider">Divisions</div>
                <div className="col-span-3 text-xs font-medium text-text-secondary uppercase tracking-wider">Grades</div>
                <div className="col-span-2 text-xs font-medium text-text-secondary uppercase tracking-wider">Annual Tuition</div>
                <div className="col-span-2 text-xs font-medium text-text-secondary uppercase tracking-wider">Registration Fee</div>
                <div className="col-span-2 text-xs font-medium text-text-secondary uppercase tracking-wider">Security Fee</div>
              </div>

              {tuitionRates.map((rate) => (
                <div key={rate.id} className="mb-3 grid grid-cols-12 items-center gap-4 rounded-md border border-border bg-muted p-3">
                  <div className="col-span-3 relative">
                    <MultiSelect
                      options={DIVISION_OPTIONS}
                      value={rate.divisions}
                      onChange={(value) => addToRate(rate.id, 'divisions', value)}
                      onRemove={(value) => removeChip(rate.id, 'divisions', value)}
                      placeholder="Select Divisions..."
                    />
                  </div>
                  <div className="col-span-3 relative">
                    <MultiSelect
                      options={GRADE_OPTIONS}
                      value={rate.grades}
                      onChange={(value) => addToRate(rate.id, 'grades', value)}
                      onRemove={(value) => removeChip(rate.id, 'grades', value)}
                      placeholder="Select Grades..."
                    />
                  </div>
                  <div className="col-span-2 relative">
                    <span className="absolute left-3 top-2 text-text-secondary text-sm">$</span>
                    <input
                      className="block w-full rounded border-border pl-7 text-sm h-9 border"
                      type="text"
                      value={rate.tuition}
                      onChange={(event) => updateTuitionRate(rate.id, { tuition: event.target.value })}
                      onBlur={() => void persistTuitionRates(tuitionRates)}
                    />
                  </div>
                  <div className="col-span-2 relative">
                    <span className="absolute left-3 top-2 text-text-secondary text-sm">$</span>
                    <input
                      className="block w-full rounded border-border pl-7 text-sm h-9 border"
                      type="text"
                      value={rate.reg}
                      onChange={(event) => updateSharedTuitionField('reg', event.target.value)}
                      onBlur={() => void persistTuitionRates(tuitionRates)}
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-2 relative">
                    <span className="absolute left-3 top-2 text-text-secondary text-sm">$</span>
                    <input
                      className="block w-full rounded border-border pl-7 text-sm h-9 border"
                      type="text"
                      value={rate.security}
                      onChange={(event) => updateSharedTuitionField('security', event.target.value)}
                      onBlur={() => void persistTuitionRates(tuitionRates)}
                    />
                    <button
                      onClick={() => removeTuitionRate(rate.id)}
                      className="text-text-tertiary hover:text-strawberry ml-2"
                    >
                      <Icon name="Trash2" className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Default Fees sub-card */}
          <div
            data-testid="settings-admissions-default-fees"
            className="mt-6 rounded-lg border border-border bg-muted/40 p-4"
          >
            <h5 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-4">
              Default Fees
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-irongrey mb-1">
                  Family Assessment
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-text-secondary text-sm">$</span>
                  <input
                    className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-10 pl-7 pr-3 border"
                    type="text"
                    defaultValue="0"
                    aria-label="Family Assessment fee"
                    onBlur={() => void persistFees(fees)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-irongrey mb-1">
                  Building Fund
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-text-secondary text-sm">$</span>
                  <input
                    className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-10 pl-7 pr-3 border"
                    type="text"
                    defaultValue="0"
                    aria-label="Building Fund fee"
                    onBlur={() => void persistFees(fees)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-irongrey mb-1">
                  Security Fee
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-text-secondary text-sm">$</span>
                  <input
                    className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-10 pl-7 pr-3 border"
                    type="text"
                    value={tuitionRates[0]?.security ?? '0'}
                    onChange={(e) => updateSharedTuitionField('security', e.target.value)}
                    onBlur={() => void persistTuitionRates(tuitionRates)}
                    aria-label="Security Fee"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Multi-child discount footnote */}
          <div className="mt-6">
            <Textarea
              label="Multi-child discount footnote"
              subLabel="Displayed on the family portal tuition breakdown."
              maxLength={200}
              value={discountFootnote}
              onChange={(e) => setDiscountFootnote(e.target.value)}
              placeholder="e.g. A 10% discount applies when three or more children from the same family are enrolled simultaneously."
              rows={3}
            />
          </div>
        </div>
      </Card>

      <Card>
        <SectionHeader
          title="Admissions Tuition Defaults"
          description="These settings seed the new Admissions Tuition tab and its contract generation flow."
        />

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="rounded-lg border border-border bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-irongrey">Rate Categories</h4>
                <p className="mt-1 text-xs text-text-secondary">
                  Default category: {settingsState.defaultTuitionRateCategoryId || 'not set'}
                </p>
              </div>
              <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                {tuitionRateCategories.length} configured
              </span>
            </div>

            <div className="space-y-4">
              {tuitionRateCategories.map((category) => (
                <div
                  key={category.id}
                  className="rounded-lg border border-border bg-muted/40 p-4"
                  data-testid={`settings-admissions-tuition-category-${category.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-irongrey">{category.label}</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        {category.description || 'No description provided.'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {category.flatRateMode ? (
                        <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-irongrey">
                          Flat rate
                        </span>
                      ) : null}
                      {category.multiChildDiscountEligible ? (
                        <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-irongrey">
                          Multi-child
                        </span>
                      ) : null}
                      {category.financialAidEligible ? (
                        <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-irongrey">
                          Financial aid
                        </span>
                      ) : null}
                      {category.staffDiscountEligible ? (
                        <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-irongrey">
                          Staff
                        </span>
                      ) : null}
                      {category.chinuchDiscountEligible ? (
                        <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-irongrey">
                          Chinuch
                        </span>
                      ) : null}
                      {category.fundraisingObligationEnabled ? (
                        <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-irongrey">
                          Fundraising
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-md bg-white p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">Tuition bands</p>
                      <p className="mt-1 text-sm font-semibold text-irongrey">{category.tuitionRates.length}</p>
                    </div>
                    <div className="rounded-md bg-white p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">Fee lines</p>
                      <p className="mt-1 text-sm font-semibold text-irongrey">{category.feeSchedule.length}</p>
                    </div>
                    <div className="rounded-md bg-white p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">Fundraising / child</p>
                      <p className="mt-1 text-sm font-semibold text-irongrey">
                        ${Number(category.fundraisingAmountPerChild ?? 0).toLocaleString('en-US')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {tuitionRateCategories.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm text-text-secondary">
                  No tuition rate categories are configured yet.
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-5">
            <h4 className="text-sm font-semibold text-irongrey">Enabled Payment Plans</h4>
            <p className="mt-1 text-xs text-text-secondary">
              These plans appear in the Admissions Tuition totals section.
            </p>

            <div className="mt-4 space-y-3">
              {enabledTuitionPaymentPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="rounded-lg border border-border bg-muted/40 p-4"
                  data-testid={`settings-admissions-payment-plan-${plan.id}`}
                >
                  <p className="text-sm font-semibold text-irongrey">{plan.label}</p>
                  <p className="mt-1 text-xs text-text-secondary">
                    {plan.installmentCount} installments • {plan.startDate} to {plan.endDate}
                  </p>
                </div>
              ))}

              {enabledTuitionPaymentPlans.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm text-text-secondary">
                  No payment plans are enabled.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Card>

      <Modal isOpen={isFeeModalOpen} onClose={() => setIsFeeModalOpen(false)} title={editingFee ? 'Edit Fee Type' : 'Add Fee Type'}>
        <div className="space-y-4">
          <Input label="Fee Name" value={feeForm.name} onChange={(event) => setFeeForm({ ...feeForm, name: event.target.value })} placeholder="e.g. Building Fund" />

          <div className="flex items-end gap-4">
            <div className="flex-grow">
              <label className="block text-sm font-medium text-irongrey mb-1">Base Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-text-secondary text-sm">$</span>
                <input
                  className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-10 pl-7 pr-3 border"
                  value={feeForm.amount}
                  onChange={(event) => setFeeForm({ ...feeForm, amount: event.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Toggle checked={feeForm.config === 'Per Child'} onChange={() => setFeeForm({ ...feeForm, config: feeForm.config === 'Per Child' ? 'Per Family' : 'Per Child' })} label="" requiredText={false} />
              <span className="text-sm text-irongrey font-medium">Per Child</span>
            </div>
          </div>

          <div className="pt-2 mt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-bold text-irongrey">Fee Rules</label>
              <Button variant="outline" className="text-xs !px-2 !py-1" onClick={addRule}>
                <Icon name="Plus" className="w-3 h-3 mr-1" /> Add Rule
              </Button>
            </div>

            <div className="space-y-3">
              {feeForm.rules.map((rule) => (
                <div key={rule.id} className="border border-border rounded-lg p-4 relative bg-white shadow-sm">
                  <button onClick={() => removeRule(rule.id)} className="absolute top-3 right-3 text-text-tertiary hover:text-strawberry">
                    <Icon name="Trash2" className="w-4 h-4" />
                  </button>

                  <div className="flex gap-6 mb-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="radio"
                        name={`rule-type-${rule.id}`}
                        checked={rule.type === 'duration'}
                        onChange={() => updateRule(rule.id, { type: 'duration' })}
                        className="text-primary focus:ring-primary h-4 w-4 border-border"
                      />
                      <span className="text-irongrey font-medium">Duration Limit</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="radio"
                        name={`rule-type-${rule.id}`}
                        checked={rule.type === 'multichild'}
                        onChange={() => updateRule(rule.id, { type: 'multichild' })}
                        className="text-primary focus:ring-primary h-4 w-4 border-border"
                      />
                      <span className="text-irongrey font-medium">Multi-Child Discount</span>
                    </label>
                  </div>

                  {rule.type === 'duration' && (
                    <div className="flex items-center gap-2 text-sm text-irongrey">
                      Apply fee for the first
                      <input
                        className="w-16 h-8 border border-border rounded text-center focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                        value={rule.years || ''}
                        onChange={(event) => updateRule(rule.id, { years: event.target.value })}
                        placeholder="0"
                      />
                      years.
                    </div>
                  )}

                  {rule.type === 'multichild' && (
                    <div className="flex items-center flex-wrap gap-2 text-sm text-irongrey leading-relaxed">
                      When family has
                      <input
                        className="w-16 h-8 border border-border rounded text-center focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                        value={rule.childCount || ''}
                        onChange={(event) => updateRule(rule.id, { childCount: event.target.value })}
                        placeholder="0"
                      />
                      or more children, apply
                      <input
                        className="w-16 h-8 border border-border rounded text-center focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                        value={rule.discountPercent || ''}
                        onChange={(event) => updateRule(rule.id, { discountPercent: event.target.value })}
                        placeholder="0"
                      />
                      % discount.
                    </div>
                  )}
                </div>
              ))}
              {feeForm.rules.length === 0 && (
                <div className="text-center py-6 bg-muted rounded-lg border border-dashed border-border">
                  <p className="text-sm text-text-secondary">No conditional rules applied.</p>
                  <p className="text-xs text-text-tertiary mt-1">Click "Add Rule" to configure discounts or duration limits.</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
            <Button variant="ghost" onClick={() => setIsFeeModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleFeeSubmit} className="bg-iron-grey hover:bg-irongrey !text-white !border-transparent">Save Fee</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
